import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input, Select, Textarea } from '@/components/ui/Field';
import { Banner, EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { usePeriod } from '@/app/PeriodSwitcher';
import { FileUpload } from '@/components/ui/FileUpload';
import { settingsApi } from '@/features/settings/api';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  FINDING_KIND_LABEL,
  FINDING_STATUS_LABEL,
  KIND_LABELS,
  SUBJECT_CATEGORY_LABEL,
  monevApi,
  type Finding,
  type FindingInput,
  type FindingKind,
  type FindingStatus,
  type SubjectCategory,
} from './api';

/** L-09a — Risalah monev: ketidaksesuaian & keberhasilan, plus tindak lanjutnya. */
export function FindingsPage() {
  const queryClient = useQueryClient();
  const { periods, current, currentId, isAll } = usePeriod();

  const [editing, setEditing] = useState<Finding | null | undefined>(undefined);
  const [openId, setOpenId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Finding | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const activePeriodId =
    current?.id ?? periods.find((p) => p.status === 'active')?.id ?? periods[0]?.id ?? '';

  const { data, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['findings', currentId, statusFilter],
    queryFn: () =>
      monevApi.listFindings({
        periodId: isAll ? undefined : (current?.id ?? undefined),
        status: statusFilter || undefined,
      }),
    enabled: true,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['findings'] });
  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Gagal menyimpan perubahan.');

  const save = useMutation({
    mutationFn: async (input: FindingInput) => {
      if (editing) {
        await monevApi.updateFinding(editing.id, input);
        return null;
      }
      return monevApi.createFinding(input);
    },
    onSuccess: (created) => {
      refresh();
      setError(null);
      setEditing(undefined);
      setBanner(
        created
          ? `Risalah ${created.code} tersimpan. Buka kartunya untuk melampirkan berkas.`
          : 'Risalah tersimpan.',
      );
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => monevApi.deleteFinding(deleting!.id),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      setBanner('Risalah dihapus.');
    },
    onError: (err) => {
      setDeleting(null);
      fail(err);
    },
  });

  const rows = data ?? [];
  const counts = {
    open: rows.filter((r) => r.status === 'open').length,
    in_progress: rows.filter((r) => r.status === 'in_progress').length,
    resolved: rows.filter((r) => r.status === 'resolved').length,
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Risalah Monev"
        description={
          isAll
            ? 'Ketidaksesuaian dan keberhasilan yang tercatat saat monev · Semua Semester'
            : `Ketidaksesuaian dan keberhasilan yang tercatat saat monev · ${current?.name ?? '—'}`
        }
        action={<Button onClick={() => setEditing(null)}>+ Risalah Baru</Button>}
      />

      {banner && <Banner tone="success">{banner}</Banner>}
      {error && editing === undefined && <Banner tone="danger">{error}</Banner>}

      <div className="flex flex-wrap gap-2">
        {(['', 'open', 'in_progress', 'resolved'] as const).map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'min-h-10 rounded-full border px-4 text-sm font-medium',
              statusFilter === s
                ? 'border-primary bg-primary-soft text-primary'
                : 'border-border text-ink-secondary hover:border-primary',
            )}
          >
            {s === '' ? `Semua (${rows.length})` : `${FINDING_STATUS_LABEL[s]} (${counts[s]})`}
          </button>
        ))}
      </div>

      {loadError ? (
        <ErrorState message={(loadError as Error).message} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            icon="🔍"
            title="Belum ada risalah di periode ini"
            description="Catat apa yang perlu ditindaklanjuti setelah kunjungan monev."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((row) => (
            <FindingCard
              key={row.id}
              finding={row}
              expanded={openId === row.id}
              onToggle={() => setOpenId(openId === row.id ? null : row.id)}
              onEdit={() => setEditing(row)}
              onDelete={() => setDeleting(row)}
              onChanged={(message) => {
                refresh();
                setBanner(message);
              }}
              onError={fail}
            />
          ))}
        </div>
      )}

      {editing !== undefined && (
        <FindingEditor
          finding={editing}
          periodId={activePeriodId}
          saving={save.isPending}
          error={error}
          onCancel={() => {
            setEditing(undefined);
            setError(null);
          }}
          onSave={(input) => save.mutate(input)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Hapus risalah?"
        confirmLabel="Ya, hapus"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
      >
        <p className="mb-3 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 text-ink">
          {deleting?.description}
        </p>
        <p>Seluruh linimasa tindak lanjutnya ikut hilang dan tidak bisa dikembalikan.</p>
      </ConfirmDialog>
    </div>
  );
}

function FindingCard({
  finding,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onChanged,
  onError,
}: {
  finding: Finding;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onChanged: (message: string) => void;
  onError: (err: unknown) => void;
}) {
  const [target, setTarget] = useState<FindingStatus | null>(null);

  const { data: detail } = useQuery({
    queryKey: ['finding', finding.id],
    queryFn: () => monevApi.getFinding(finding.id),
    enabled: expanded,
  });

  const move = useMutation({
    mutationFn: (status: FindingStatus) => monevApi.setFindingStatus(finding.id, status),
    onSuccess: (_, status) => {
      setTarget(null);
      onChanged(`Risalah dipindahkan ke ${FINDING_STATUS_LABEL[status]}.`);
    },
    onError,
  });

  const overdue =
    finding.status !== 'resolved' &&
    finding.dueDate !== null &&
    finding.dueDate < new Date().toISOString().slice(0, 10);

  return (
    <Card padded={false} className="min-w-0 transition-shadow hover:shadow-sm">
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 text-left cursor-pointer transition-colors hover:bg-app/40"
        aria-expanded={expanded}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-[12px] font-semibold border',
                  finding.kind === 'nonconformity'
                    ? 'bg-[#fff1d6] text-[#7a3b00] border-[#f5a623]/50'
                    : 'bg-[#def5eb] text-[#065f46] border-[#1ea97c]/50',
                )}
              >
                {FINDING_KIND_LABEL[finding.kind]}
              </span>
              <StatusChip status={finding.status} />
              <span className="tabular text-[13px] font-medium text-ink-secondary">
                {finding.code}
              </span>
              <span className="text-[13px] text-ink-secondary">· {finding.regionName}</span>
              {finding.subjectCategory && (
                <span className="text-[13px] text-ink-secondary">
                  · {SUBJECT_CATEGORY_LABEL[finding.subjectCategory]}
                </span>
              )}
              {overdue && (
                <span className="rounded-full bg-danger-soft px-2 py-0.5 text-[12px] font-medium text-danger">
                  lewat tenggat
                </span>
              )}
            </div>
            <p className="mt-2 text-[15px] font-medium text-ink leading-snug">{finding.description}</p>
            <p className="mt-1 text-[13px] text-ink-secondary">
              Asesor: <strong className="font-medium text-ink">{finding.assessorName ?? '—'}</strong>
              {finding.kind === 'nonconformity' && (
                <> · Batas waktu: <span className="tabular font-medium text-ink">{finding.dueDate ?? '—'}</span></>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 rounded-lg px-2.5 py-1 text-[13px] font-medium text-primary hover:bg-primary-soft transition-colors">
            <span>{expanded ? 'Tutup Rincian' : 'Lihat Rincian'}</span>
            <span className="text-base leading-none" aria-hidden>
              {expanded ? '▴' : '▾'}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border bg-app px-5 py-4">
          {finding.weakIndicator && (
            <Detail label="Indikator Terkait" value={finding.weakIndicator} />
          )}
          {detail?.rootCause && (
            <Detail label={KIND_LABELS[finding.kind].rootCause} value={detail.rootCause} />
          )}
          {detail?.correction && <Detail label="Koreksi Langsung (Correction)" value={detail.correction} />}
          {detail?.correctiveAction && (
            <Detail label="Tindakan Korektif (Corrective Action)" value={detail.correctiveAction} />
          )}
          {finding.kind === 'success' && detail?.evidenceNote && (
            <Detail label={KIND_LABELS.success.evidence} value={detail.evidenceNote} />
          )}

          {/* Keberhasilan sudah terbukti sejak dicatat, jadi berkasnya tersedia
              begitu kartunya dibuka — tanpa menunggu tindak lanjut apa pun. */}
          {finding.kind === 'success' && (
            <div className="mb-4 rounded-[var(--radius-input)] border border-border bg-surface p-3.5">
              <FileUpload
                entityType="monev_finding"
                entityId={finding.id}
                label="Berkas Bukti Keberhasilan"
                hint="JPG, PNG, WebP, atau PDF · maksimal 5 MB"
              />
            </div>
          )}

          <p className="text-[13px] font-semibold text-ink-secondary">Linimasa tindak lanjut</p>
          <ol className="mt-2 flex flex-col gap-2.5">
            {(detail?.logs ?? []).map((log) => (
              <li key={log.id} className="flex gap-3">
                <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" aria-hidden />
                <div className="min-w-0">
                  <p className="text-[13px] text-ink">
                    <strong>{log.toStatus ? FINDING_STATUS_LABEL[log.toStatus as FindingStatus] : '—'}</strong>
                    {log.byName && <span className="text-ink-secondary"> · {log.byName}</span>}
                    <span className="text-ink-muted"> · {log.createdAt.slice(0, 10)}</span>
                  </p>
                  {log.comment && <p className="text-[14px] text-ink-secondary">{log.comment}</p>}
                </div>
              </li>
            ))}
          </ol>

          {/*
            Bukti Penyelesaian baru masuk akal setelah risalah ditindaklanjuti.
            Selama masih 'Terbuka' belum ada yang dikerjakan, jadi tidak ada
            yang bisa dibuktikan; menampilkannya sejak awal hanya mengundang
            kotak kosong yang tidak pernah terisi.
          */}
          {finding.kind === 'nonconformity' && finding.status !== 'open' && (
            <ResolutionEvidence finding={finding} note={detail?.evidenceNote ?? ''} />
          )}

          {/*
            Tidak ada lagi "Catatan tindak lanjut" terpisah.

            Isinya selalu mengulang Bukti Penyelesaian yang baru saja diketik
            beberapa sentimeter di atasnya, dan linimasa memang lebih baik
            mengutip bukti itu daripada catatan kedua yang ditulis asal supaya
            tombol Tutup aktif.

            "Tutup Risalah" juga sengaja TIDAK ada saat status masih Terbuka:
            menutup sesuatu yang belum pernah ditindaklanjuti berarti menutupnya
            tanpa bukti apa pun.
          */}
          {finding.status !== 'resolved' && (
            <div className="mt-4 flex flex-wrap gap-2">
              {finding.status === 'open' ? (
                <Button size="sm" variant="secondary" onClick={() => setTarget('in_progress')}>
                  Tandai Ditindaklanjuti
                </Button>
              ) : (
                <Button size="sm" onClick={() => setTarget('resolved')}>
                  Tutup Risalah
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onEdit}>
                Ubah
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete}>
                Hapus
              </Button>
            </div>
          )}

          {finding.status === 'resolved' && (
            <div className="mt-4 flex gap-2">
              {/* Keberhasilan lahir berstatus 'resolved' dan memang tidak punya
                  tindak lanjut — server menolak perpindahan statusnya. Tombol
                  "Buka Kembali" di sini hanya menjanjikan aksi yang pasti gagal. */}
              {finding.kind === 'nonconformity' && (
                <Button size="sm" variant="ghost" onClick={() => setTarget('in_progress')}>
                  Buka Kembali
                </Button>
              )}
              {/* Ubah harus tetap ada: tanpa ini risalah keberhasilan — yang
                  SELALU 'resolved' — tidak akan pernah bisa disunting sama sekali. */}
              <Button size="sm" variant="ghost" onClick={onEdit}>
                Ubah
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete}>
                Hapus
              </Button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={target !== null}
        title={
          target === 'resolved'
            ? 'Tutup risalah ini?'
            : target === 'in_progress' && finding.status === 'resolved'
              ? 'Buka kembali risalah?'
              : 'Tandai sedang ditindaklanjuti?'
        }
        confirmLabel="Ya, lanjut"
        tone="primary"
        loading={move.isPending}
        onCancel={() => setTarget(null)}
        onConfirm={() => move.mutate(target!)}
      >
        <p>
          Perpindahan status ini dicatat di linimasa beserta nama Anda dan waktunya.
          {target === 'resolved' && ' Bukti Penyelesaian ikut dikutip sebagai catatannya.'}
        </p>
      </ConfirmDialog>
    </Card>
  );
}

function StatusChip({ status }: { status: FindingStatus }) {
  return (
    <span
      className={cn(
        'rounded-full px-2.5 py-0.5 text-[12px] font-medium',
        status === 'resolved'
          ? 'bg-success-soft text-success'
          : status === 'in_progress'
            ? 'bg-primary-soft text-primary'
            : 'bg-warn-soft text-[#9a6400]',
      )}
    >
      {FINDING_STATUS_LABEL[status]}
    </span>
  );
}

/**
 * Form risalah monev.
 *
 * Bentuknya ditentukan JENIS RISALAH yang dipilih paling atas. Ketidaksesuaian
 * menuntut akar masalah, koreksi, tindakan korektif, dan batas waktu;
 * keberhasilan hanya menanyakan faktor pendukung dan buktinya. Sisanya
 * disembunyikan alih-alih dibiarkan kosong, supaya yang terlihat di layar
 * benar-benar yang perlu diisi.
 */
/**
 * Bukti penyelesaian sebuah ketidaksesuaian — catatan plus berkasnya.
 *
 * Terpisah dari form risalah karena hidup di fase yang berbeda: risalahnya
 * dicatat saat monev, buktinya menyusul saat perbaikan benar-benar dikerjakan.
 */
function ResolutionEvidence({ finding, note }: { finding: Finding; note: string }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(note);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(note);
    setSaved(false);
  }, [note, finding.id]);

  const save = useMutation({
    mutationFn: () => monevApi.updateFinding(finding.id, { evidenceNote: value.trim() || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finding', finding.id] });
      setSaved(true);
    },
  });

  return (
    <div className="mt-4 rounded-[var(--radius-input)] border border-border bg-surface p-3.5">
      <Textarea
        label="Bukti Penyelesaian"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setSaved(false);
        }}
        hint="Apa yang sudah dikerjakan dan hasilnya. Berkasnya dilampirkan di bawah."
      />
      <div className="mt-2 flex items-center gap-3">
        <Button
          size="sm"
          variant="secondary"
          loading={save.isPending}
          onClick={() => save.mutate()}
          disabled={value.trim() === note.trim()}
        >
          Simpan bukti
        </Button>
        {saved && <span className="text-[13px] text-success">Tersimpan.</span>}
      </div>

      <div className="mt-3">
        <FileUpload
          entityType="monev_finding"
          entityId={finding.id}
          label="Berkas Bukti Penyelesaian"
          hint="JPG, PNG, WebP, atau PDF · maksimal 5 MB"
        />
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-4">
      <p className="text-[13px] font-semibold text-ink-secondary">{label}</p>
      <p className="mt-0.5 text-[15px] whitespace-pre-line text-ink">{value}</p>
    </div>
  );
}

function FindingEditor({
  finding,
  periodId,
  saving,
  error,
  onCancel,
  onSave,
}: {
  finding: Finding | null;
  periodId: string;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: FindingInput) => void;
}) {
  const [kind, setKind] = useState<FindingKind>('nonconformity');
  const [regionId, setRegionId] = useState('');
  const [foundDate, setFoundDate] = useState('');
  const [weakIndicator, setWeakIndicator] = useState('');
  const [subjectCategory, setSubjectCategory] = useState<SubjectCategory | ''>('');
  const [description, setDescription] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [correction, setCorrection] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [assessorId, setAssessorId] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** Kabupaten saja — jenjang wilayah lain bukan satuan kerja monev. */
  const { data: regionList } = useQuery({
    queryKey: ['regions', 'city'],
    queryFn: () => settingsApi.listRegions({ level: 'city' }),
  });

  const { data: userList } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => settingsApi.listUsers(),
  });

  const assessors = (userList ?? []).filter((u) => u.role === 'assessor' && u.isActive);

  // Baris daftar tidak memuat isian panjangnya; detailnya diambil terpisah saat
  // menyunting supaya kotak yang sudah pernah diisi tidak tampil kosong.
  const { data: detail } = useQuery({
    queryKey: ['finding', finding?.id],
    queryFn: () => monevApi.getFinding(finding!.id),
    enabled: Boolean(finding),
  });

  useEffect(() => {
    setKind(finding?.kind ?? 'nonconformity');
    setRegionId(finding?.regionId ?? '');
    setFoundDate(finding?.foundDate ?? '');
    setWeakIndicator(finding?.weakIndicator ?? '');
    setSubjectCategory(finding?.subjectCategory ?? '');
    setDescription(finding?.description ?? '');
    setDueDate(finding?.dueDate ?? '');
    setFieldErrors({});
  }, [finding]);

  useEffect(() => {
    if (!detail) return;
    setRootCause(detail.rootCause ?? '');
    setCorrection(detail.correction ?? '');
    setCorrectiveAction(detail.correctiveAction ?? '');
    setEvidenceNote(detail.evidenceNote ?? '');
    setAssessorId(detail.assessorId ?? '');
  }, [detail]);

  const isNonconformity = kind === 'nonconformity';
  const labels = KIND_LABELS[kind];

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!regionId) errors.regionId = 'Wilayah wajib dipilih';
    if (!description.trim()) errors.description = 'Uraian wajib diisi';
    if (isNonconformity && foundDate && dueDate && dueDate < foundDate) {
      errors.dueDate = 'Batas waktu mendahului tanggal monev';
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    onSave({
      kind,
      periodId,
      regionId,
      foundDate: foundDate || null,
      weakIndicator: weakIndicator.trim() || null,
      subjectCategory: subjectCategory || null,
      description: description.trim(),
      rootCause: rootCause.trim() || null,
      // Isian khusus ketidaksesuaian dikosongkan di sini juga, bukan hanya
      // disembunyikan — server memang membuangnya, tapi mengirim yang sudah
      // benar membuat permintaannya jujur terhadap apa yang terlihat.
      correction: isNonconformity ? correction.trim() || null : null,
      correctiveAction: isNonconformity ? correctiveAction.trim() || null : null,
      dueDate: isNonconformity ? dueDate || null : null,
      // Ketidaksesuaian mengisi buktinya lewat panel tindak lanjut, jadi
      // nilai lama dipertahankan apa adanya — jangan tertimpa kosong.
      evidenceNote: isNonconformity ? (detail?.evidenceNote ?? null) : evidenceNote.trim() || null,
      assessorId: assessorId || null,
    });
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-[var(--radius-card)] bg-surface shadow-[var(--shadow-hover)]">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-[19px] font-semibold text-ink">
            {finding ? `Ubah Risalah ${finding.code}` : 'Risalah Baru'}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {error && (
            <div className="mb-4">
              <Banner tone="danger">{error}</Banner>
            </div>
          )}

          <Select
            label="Kategori risalah"
            value={kind}
            onChange={(e) => setKind(e.target.value as FindingKind)}
            hint="Menentukan isian yang muncul di bawah."
            required
          >
            <option value="nonconformity">A. Ketidaksesuaian</option>
            <option value="success">B. Keberhasilan</option>
          </Select>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Input
              label="Kode risalah"
              value={finding?.code ?? 'otomatis setelah disimpan'}
              readOnly
              disabled
              hint="Dibuat sistem, tidak bisa diubah."
            />
            <Input
              label="Tanggal monev"
              type="date"
              value={foundDate}
              onChange={(e) => setFoundDate(e.target.value)}
            />
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Select
              label="Wilayah"
              value={regionId}
              onChange={(e) => setRegionId(e.target.value)}
              error={fieldErrors.regionId}
              required
            >
              <option value="">Pilih kabupaten</option>
              {(regionList ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
            <Select
              label="Kategori"
              value={subjectCategory}
              onChange={(e) => setSubjectCategory(e.target.value as SubjectCategory | '')}
            >
              <option value="">Pilih kategori</option>
              <option value="pelita">A. Perkembangan PELITA SLI</option>
              <option value="tbm">B. Perkembangan TBM</option>
            </Select>
          </div>

          <div className="mt-4">
            <Textarea
              label="Indikator yang belum berjalan baik"
              value={weakIndicator}
              onChange={(e) => setWeakIndicator(e.target.value)}
              hint="Sebutkan indikator dari instrumen yang jadi sorotan."
            />
          </div>

          <div className="mt-4">
            <Textarea
              label="Uraian"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={fieldErrors.description}
              hint="Apa yang teramati di lapangan — fakta, bukan penilaian."
              required
            />
          </div>

          <div className="mt-4">
            <Textarea
              label={labels.rootCause}
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              hint={
                isNonconformity
                  ? 'Kenapa hal ini terjadi — bukan gejalanya.'
                  : 'Apa yang membuat ini berhasil, supaya bisa ditiru wilayah lain.'
              }
            />
          </div>

          {isNonconformity && (
            <>
              <div className="mt-4">
                <Textarea
                  label="Correction"
                  value={correction}
                  onChange={(e) => setCorrection(e.target.value)}
                  hint="Tindakan langsung menambal gejalanya."
                />
              </div>

              <div className="mt-4">
                <Textarea
                  label="Corrective Action"
                  value={correctiveAction}
                  onChange={(e) => setCorrectiveAction(e.target.value)}
                  hint="Tindakan agar hal yang sama tidak terulang."
                />
              </div>

              <div className="mt-4">
                <Input
                  label="Batas waktu perbaikan"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  error={fieldErrors.dueDate}
                />
              </div>
            </>
          )}

          {/*
            Bukti hanya ada di sini untuk KEBERHASILAN.

            Keberhasilan sudah terbukti pada saat dicatat, jadi buktinya memang
            bagian dari pencatatan. Ketidaksesuaian sebaliknya: bukti
            PENYELESAIAN belum mungkin ada sebelum kasusnya ditindaklanjuti —
            isiannya muncul di panel tindak lanjut, bukan di sini.
          */}
          {!isNonconformity && (
            <>
              <div className="mt-4">
                <Textarea
                  label={labels.evidence}
                  value={evidenceNote}
                  onChange={(e) => setEvidenceNote(e.target.value)}
                  hint="Keterangan buktinya. Berkasnya diunggah di bawah."
                />
              </div>

              {/* Berkasnya dilampirkan dari KARTU risalah, bukan dari form.
                  Lampiran menempel pada id risalah — yang belum ada saat form
                  masih diisi — dan memaksa form tetap terbuka setelah simpan
                  hanya memunculkan modal "Ubah" yang tidak diminta siapa pun. */}
              <p className="mt-2 text-[13px] text-ink-muted">
                Berkas buktinya dilampirkan dari kartu risalah setelah ini disimpan.
              </p>
            </>
          )}

          <div className="mt-4">
            <Select
              label="Asesor"
              value={assessorId}
              onChange={(e) => setAssessorId(e.target.value)}
              hint={
                assessors.length === 0
                  ? 'Belum ada pengguna berperan Assessor — tambahkan di Pengaturan → Pengguna.'
                  : undefined
              }
            >
              <option value="">Pilih asesor</option>
              {assessors.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2.5 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
          {/* "Batal" hanya jujur pada risalah yang belum tersimpan. Begitu
              barisnya ada — termasuk sesaat setelah dibuat — menutup form tidak
              membatalkan apa pun, dan labelnya tidak boleh menyiratkan itu. */}
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {finding ? 'Tutup' : 'Batal'}
          </Button>
          <Button type="submit" loading={saving}>
            {finding ? 'Simpan Perubahan' : 'Simpan Risalah'}
          </Button>
        </div>
      </div>
    </form>
  );
}
